import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useSearchParams } from 'react-router'

import type { Region } from '@/pages/espelho-de-vendas-horizontal/map/google-map-with-image-overlay'
import type { Coordinate, TGetBlocosResponse } from '@/types/blocos'

const MAX_ZOOM_LEVEL = 22
const MERCATOR_TILE_SIZE = 256

function calculateMinZoom(
  north: number,
  south: number,
  west: number,
  east: number,
  containerWidth: number,
  containerHeight: number,
): number {
  const lngSpan = east - west

  const mercatorNorth = Math.log(Math.tan(Math.PI / 4 + (north * Math.PI) / 360))
  const mercatorSouth = Math.log(Math.tan(Math.PI / 4 + (south * Math.PI) / 360))
  const mercatorSpan = mercatorNorth - mercatorSouth

  const zoomLng = Math.log2((containerWidth * 360) / (MERCATOR_TILE_SIZE * lngSpan))
  const zoomLat = Math.log2((containerHeight * 2 * Math.PI) / (MERCATOR_TILE_SIZE * mercatorSpan))

  return Math.min(zoomLng, zoomLat)
}

interface UseGoogleMapOverlayParams {
  overlayCoordinates: [Coordinate, Coordinate]
  quadras: TGetBlocosResponse['blocos']
  initialRegion: Region
  baseLatLngDelta: number
  onRegionChange?: (region: Region) => void
}

export function useGoogleMapOverlay({
  overlayCoordinates,
  quadras,
  initialRegion,
  baseLatLngDelta,
  onRegionChange,
}: UseGoogleMapOverlayParams) {
  const [searchParams] = useSearchParams()
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerSize, setContainerSize] = useState<{ width: number; height: number } | null>(null)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const observer = new ResizeObserver((entries) => {
      if (entries.length === 0) return
      const { width, height } = entries[0]!.contentRect
      if (width > 0 && height > 0) {
        setContainerSize({ width, height })
      }
    })

    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const minZoomLevel = useMemo(() => {
    if (!containerSize) return null

    const [northWest, southEast] = overlayCoordinates
    return calculateMinZoom(
      northWest.latitude,
      southEast.latitude,
      northWest.longitude,
      southEast.longitude,
      containerSize.width,
      containerSize.height,
    )
  }, [containerSize, overlayCoordinates])

  const [currentZoom, setCurrentZoom] = useState<number | null>(null)

  useEffect(() => {
    if (minZoomLevel !== null && currentZoom === null) {
      setCurrentZoom(minZoomLevel)
    }
  }, [minZoomLevel, currentZoom])

  const handleCameraChange = useCallback(
    (event: { detail: { zoom?: number; center?: { lat: number; lng: number } } }) => {
      const newZoom = event.detail.zoom ?? currentZoom ?? minZoomLevel ?? MAX_ZOOM_LEVEL
      setCurrentZoom(newZoom)

      if (onRegionChange && event.detail.center) {
        const center = event.detail.center
        onRegionChange({
          latitude: center.lat,
          longitude: center.lng,
          latitudeDelta: baseLatLngDelta,
          longitudeDelta: baseLatLngDelta,
        })
      }
    },
    [baseLatLngDelta, currentZoom, minZoomLevel, onRegionChange],
  )

  const initialCenter = useMemo(() => {
    const quadraParam = searchParams.get('quadra')
    if (quadraParam) {
      const quadraIdExterno = Number.parseInt(quadraParam, 10)
      const foundQuadra = quadras.find((quadra) => quadra.idExterno === quadraIdExterno)
      if (foundQuadra) {
        return {
          lat: foundQuadra.coordenada.latitude,
          lng: foundQuadra.coordenada.longitude,
        }
      }
    }
    return {
      lat: initialRegion.latitude,
      lng: initialRegion.longitude,
    }
  }, [searchParams, initialRegion.latitude, initialRegion.longitude, quadras])

  const restrictionBounds = useMemo(() => {
    const [northWest, southEast] = overlayCoordinates
    return {
      north: northWest.latitude,
      south: southEast.latitude,
      east: southEast.longitude,
      west: northWest.longitude,
    }
  }, [overlayCoordinates])

  return {
    containerRef,
    minZoomLevel,
    currentZoom,
    handleCameraChange,
    initialCenter,
    restrictionBounds,
  }
}
